import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getProviderDisplayName } from "@/config/oauth.config";
import { markProviderLinkConfirmed } from "@/lib/oauthLinkConfirmation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import Bugsnag from "@bugsnag/js";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { PrimaryButton } from "@/components/ui/PrimaryButton";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { AppLogo } from "@/components/ui/app-logo";
import { OAuthSignInButtons } from "@/components/OAuthSignInButtons";
import { cn } from "@/lib/utils";
import { APP_CONFIG } from "@/config/app.config";
import { userService } from "@/services/userService";
import { normalizeEmailIdentity } from "@/lib/emailIdentity";

const emailSchema = z.object({
  email: z.string().min(1, "Email is required").email("Please enter a valid email address").max(100, "Email is too long"),
});

/**
 * OTP step schema. The `code` field is the only thing zod validates.
 * Privacy/Terms checkbox fields are kept on the schema (as optional booleans)
 * so the FormField wiring in the JSX still resolves, but checkbox validation
 * runs inside `handleOtpSubmit` — gated on the `acceptanceRequired` runtime
 * flag — because the schema has no access to that flag. AE-179.
 */
const otpSchema = z.object({
  code: z
    .string()
    .length(6, "Enter the 6-digit code from your email")
    .regex(/^\d{6}$/, "Code must be 6 digits"),
  privacyPolicy: z.boolean().optional(),
  termsConditions: z.boolean().optional(),
});

type EmailFormData = z.infer<typeof emailSchema>;
type OtpFormData = z.infer<typeof otpSchema>;

export const LoginPage = () => {
  const [emailLoading, setEmailLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  /** True when the OTP step must show + enforce the policy checkboxes. AE-175. */
  const [acceptanceRequired, setAcceptanceRequired] = useState(false);
  const { signInWithMagicLink, verifyOtp, isAuthenticated } = useAuth();
  const { toast } = useToast();

  const emailForm = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "" },
    mode: "onSubmit",
  });

  const otpForm = useForm<OtpFormData>({
    resolver: zodResolver(otpSchema),
    defaultValues: { code: "", privacyPolicy: false, termsConditions: false },
    mode: "onSubmit",
  });

  /** Non-null while the user is confirming a freshly linked provider. AE-206. */
  const [confirmingProvider, setConfirmingProvider] = useState<string | null>(null);
  const location = useLocation();
  const linkRequest = (location.state ?? null) as {
    confirmProvider?: string;
    confirmProviderEmail?: string;
  } | null;
  const confirmProvider = linkRequest?.confirmProvider ?? "";
  const confirmProviderEmail = linkRequest?.confirmProviderEmail ?? "";
  const confirmationStartedRef = useRef(false);

  /**
   * Whether the OTP step must show and enforce the policy checkboxes. An RPC
   * failure is non-fatal: fall back to requiring acceptance so the user can
   * still sign in, and report it as a warning. AE-175 / AE-179.
   */
  const resolveAcceptanceRequired = useCallback(async (identity: string): Promise<boolean> => {
    try {
      const existing = await userService.checkUserAcceptance(identity);
      const termsCurrent = existing?.terms_effective_date === APP_CONFIG.CURRENT_TERMS_EFFECTIVE_DATE;
      const privacyCurrent = existing?.privacy_effective_date === APP_CONFIG.CURRENT_PRIVACY_EFFECTIVE_DATE;
      return !(termsCurrent && privacyCurrent);
    } catch (lookupError) {
      Bugsnag.notify(lookupError instanceof Error ? lookupError : new Error(String(lookupError)), {
        severity: "warning",
        context: "login.check_user_acceptance",
      });
      return true;
    }
  }, []);

  /**
   * AE-206: AuthProvider withheld a session because a provider identity was
   * just linked onto a pre-existing account. Send the code once and drop the
   * user straight onto the code step. On a send failure fall back to the
   * ordinary email step rather than leaving a dead screen.
   */
  useEffect(() => {
    if (!confirmProviderEmail || confirmationStartedRef.current) return;
    confirmationStartedRef.current = true;

    void (async () => {
      setEmailLoading(true);
      try {
        const { error } = await signInWithMagicLink(confirmProviderEmail);
        if (error) {
          toast({ title: "Error", description: error.message, variant: "destructive" });
          return;
        }
        setAcceptanceRequired(await resolveAcceptanceRequired(confirmProviderEmail));
        setConfirmingProvider(confirmProvider);
        setPendingEmail(confirmProviderEmail);
      } catch (error) {
        // A genuine throw (network failure, client error) — as opposed to the
        // returned { error } handled above — would otherwise escape as an
        // unhandled rejection under `void`, leaving the user on a dead screen
        // with no toast and no report.
        Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), (event) => {
          event.severity = "error";
          event.context = "login.confirmProviderLink";
        });
        toast({ title: "Error", description: "Something went wrong", variant: "destructive" });
      } finally {
        setEmailLoading(false);
      }
    })();
  }, [confirmProvider, confirmProviderEmail, resolveAcceptanceRequired, signInWithMagicLink, toast]);

  if (isAuthenticated) {
    return <Navigate to="/" />;
  }

  const handleEmailSubmit = async (data: EmailFormData) => {
    setEmailLoading(true);
    let emailToSet = "";
    let required = true;
    const identity = normalizeEmailIdentity(data.email);
    try {
      // 1) Send the OTP. We do not block acceptance lookup on failure to send
      //    the code — if the email step succeeds, we still want to know
      //    whether to show checkboxes on the OTP screen.
      const { error } = await signInWithMagicLink(identity);
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        return;
      }
      emailToSet = identity;

      // 2) Look up the user's last-accepted effective dates. SECURITY DEFINER
      //    RPC — safe to call pre-auth via the anon key.
      required = await resolveAcceptanceRequired(identity);
    } catch (error) {
      Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), {
        severity: "error",
        context: "login.handleEmailSubmit",
      });
      toast({ title: "Error", description: "Something went wrong", variant: "destructive" });
    } finally {
      // Batch both updates so the OTP step renders with emailLoading=false,
      // ensuring the code input is never disabled when it first appears.
      setEmailLoading(false);
      if (emailToSet) {
        setAcceptanceRequired(required);
        setPendingEmail(emailToSet);
      }
    }
  };

  const handleOtpSubmit = async (data: OtpFormData) => {
    // When acceptance is required, the schema no longer enforces the checkboxes
    // (it has no access to the runtime flag), so we validate them here. AE-179.
    if (acceptanceRequired) {
      const { privacyPolicy, termsConditions } = otpForm.getValues();
      let blocked = false;
      if (privacyPolicy !== true) {
        otpForm.setError("privacyPolicy", {
          type: "manual",
          message: "You must accept the Privacy Policy to continue",
        });
        blocked = true;
      }
      if (termsConditions !== true) {
        otpForm.setError("termsConditions", {
          type: "manual",
          message: "You must accept the Terms and Conditions to continue",
        });
        blocked = true;
      }
      if (blocked) return;
    }

    setOtpLoading(true);
    try {
      // AE-206 fix 2: write the memo before calling verifyOtp, not after it
      // resolves. verifyOtp notifies onAuthStateChange with SIGNED_IN — which
      // AuthProvider.activateSession reads synchronously, before its first
      // await — strictly before this promise settles. Writing the memo after
      // verifyOtp resolved was therefore too late: if last_sign_in_at never
      // advances past created_at, activateSession re-gates the very sign-in
      // meant to confirm the link, signing the user out again with the code
      // already consumed. Trade-off: a user who enters a wrong code and then
      // abandons the flow will have the link marked confirmed on this device
      // anyway. That is acceptable — this gate is advisory, fails open by
      // design, and spec section 6 already accepts an abandonment leak.
      if (confirmingProvider) {
        markProviderLinkConfirmed(pendingEmail, confirmingProvider);
      }
      const { error, data: session } = await verifyOtp(pendingEmail, data.code);
      if (error) {
        toast({
          title: "Invalid code",
          description: "The code is incorrect or has expired. Try resending.",
          variant: "destructive",
        });
        otpForm.reset();
        return;
      }
      // onAuthStateChange in AuthProvider sets isAuthenticated → Navigate above handles redirect.
      // Acceptance persistence: do NOT block the navigation on this. If it fails
      // we still let the user into the app, but surface the error so we know
      // to investigate and so the user is aware their acceptance may not be
      // recorded for next sign-in.
      if (acceptanceRequired) {
        const userId = session?.user?.id;
        if (!userId) {
          Bugsnag.notify(new Error("verifyOtp succeeded but no user id on session"), {
            severity: "warning",
            context: "login.saveUserAcceptance",
          });
          return;
        }
        try {
          await userService.saveUserAcceptance({
            userId,
            email: pendingEmail,
            termsEffectiveDate: APP_CONFIG.CURRENT_TERMS_EFFECTIVE_DATE,
            privacyEffectiveDate: APP_CONFIG.CURRENT_PRIVACY_EFFECTIVE_DATE,
          });
        } catch (persistError) {
          Bugsnag.notify(
            persistError instanceof Error ? persistError : new Error(String(persistError)),
            { severity: "warning", context: "login.saveUserAcceptance" },
          );
          toast({
            title: "Heads up",
            description:
              "We could not save your acceptance just now — you can still continue. If asked again next time, please re-accept.",
          });
        }
      }
    } catch (error) {
      Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), {
        severity: "error",
        context: "login.handleOtpSubmit",
      });
      toast({ title: "Error", description: "Something went wrong", variant: "destructive" });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResend = async () => {
    setOtpLoading(true);
    try {
      const { error } = await signInWithMagicLink(pendingEmail);
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Code resent", description: "Check your email for a new 6-digit code" });
        otpForm.reset();
      }
    } catch (error) {
      Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), {
        severity: "warning",
        context: "login.handleResend",
      });
      toast({ title: "Error", description: "Something went wrong", variant: "destructive" });
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-8 md:gap-12 items-center mt-[12vh] min-h-screen w-full px-4 py-8">
      <AppLogo />

      {!pendingEmail ? (
        <Card key="email-step" className="w-full max-w-md mx-auto h-fit">
          <CardHeader>
            <CardTitle className="mb-1">Sign in</CardTitle>
            <CardDescription>Enter your email to receive a sign-in code</CardDescription>
          </CardHeader>

          <CardContent className="space-y-4 p-3 sm:p-6">
            <OAuthSignInButtons disabled={emailLoading} />

            <Form {...emailForm}>
              <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} noValidate className="space-y-4">
                <FormField
                  control={emailForm.control}
                  name="email"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={emailLoading}
                          type="email"
                          placeholder="your@email.com"
                          className={fieldState.error ? "border-red-500 focus-visible:ring-red-500" : ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <PrimaryButton className="w-full" disabled={emailLoading}>
                  {emailLoading ? "Sending..." : "Send sign-in code"}
                </PrimaryButton>
              </form>
            </Form>
          </CardContent>
        </Card>
      ) : (
        <Card key="otp-step" className="w-full max-w-md mx-auto h-fit">
          <CardHeader>
            <CardTitle className="mb-1">{confirmingProvider ? "Confirm it's you" : "Check your email"}</CardTitle>
            <CardDescription>
              {confirmingProvider ? (
                <>
                  To finish connecting your {getProviderDisplayName(confirmingProvider)} account, enter the 6-digit
                  code we sent to <span className="font-medium text-foreground">{pendingEmail}</span>
                </>
              ) : (
                <>
                  We sent a 6-digit code to{" "}
                  <span className="font-medium text-foreground">{pendingEmail}</span>
                </>
              )}
            </CardDescription>
          </CardHeader>

          <CardContent className="p-3 sm:p-6">
            <Form {...otpForm}>
              <form onSubmit={otpForm.handleSubmit(handleOtpSubmit)} noValidate className="space-y-4">
                <FormField
                  control={otpForm.control}
                  name="code"
                  render={({ field: { onChange: rhfOnChange, ...restField }, fieldState }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...restField}
                          onChange={(e) => {
                            // Strip non-numeric so autofill with email address resolves to ""
                            rhfOnChange(e.target.value.replace(/\D/g, "").slice(0, 6));
                          }}
                          disabled={otpLoading}
                          type="tel"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          autoFocus
                          placeholder="000000"
                          maxLength={6}
                          data-1p-ignore="true"
                          data-lpignore="true"
                          data-form-type="other"
                          className={cn("text-center text-2xl tracking-[0.4em] font-mono", {
                            "border-red-500 focus-visible:ring-red-500": fieldState.error,
                          })}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {acceptanceRequired && (
                  <>
                    <FormField
                      control={otpForm.control}
                      name="privacyPolicy"
                      render={({ field, fieldState }) => (
                        <FormItem className="flex flex-row items-start space-x-2 space-y-0 m-0">
                          <FormControl>
                            <Checkbox
                              id="privacyPolicy"
                              checked={!!field.value}
                              onCheckedChange={field.onChange}
                              disabled={otpLoading}
                              className={cn("data-[state=checked]:bg-bodhi-teal-dark", {
                                "border-red-500 focus-visible:ring-red-500": fieldState.error,
                              })}
                            />
                          </FormControl>

                          <Label
                            htmlFor="privacyPolicy"
                            className={cn(
                              "text-muted-foreground flex flex-wrap gap-1 text-xs items-center font-normal hover:cursor-pointer",
                              fieldState.error && "text-red-500",
                            )}
                          >
                            <span>I have read and agree to the</span>
                            <a
                              href={APP_CONFIG.PRIVACY_POLICY_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-semibold hover:underline"
                            >
                              Privacy Policy
                            </a>
                          </Label>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={otpForm.control}
                      name="termsConditions"
                      render={({ field, fieldState }) => (
                        <FormItem className="flex flex-row items-start space-x-2 space-y-0 !mt-2">
                          <FormControl>
                            <Checkbox
                              id="termsConditions"
                              checked={!!field.value}
                              onCheckedChange={field.onChange}
                              disabled={otpLoading}
                              className={cn("data-[state=checked]:bg-bodhi-teal-dark", {
                                "border-red-500 focus-visible:ring-red-500": fieldState.error,
                              })}
                            />
                          </FormControl>

                          <Label
                            htmlFor="termsConditions"
                            className={cn(
                              "text-muted-foreground flex flex-wrap gap-1 text-xs items-center font-normal hover:cursor-pointer",
                              fieldState.error && "text-red-500",
                            )}
                          >
                            <span>I have read and agree to the</span>
                            <a
                              href={APP_CONFIG.TERMS_AND_CONDITIONS_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-semibold hover:underline"
                            >
                              Terms and Conditions
                            </a>
                          </Label>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}

                <PrimaryButton className="w-full" disabled={otpLoading}>
                  {otpLoading ? "Verifying..." : "Sign in"}
                </PrimaryButton>

                <div className="flex justify-between text-xs text-muted-foreground pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setPendingEmail("");
                      setAcceptanceRequired(false);
                      setConfirmingProvider(null);
                      otpForm.reset();
                    }}
                    className="hover:underline"
                  >
                    Use a different email
                  </button>
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={otpLoading}
                    className="hover:underline disabled:opacity-50"
                  >
                    Resend code
                  </button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
